// First-person arms for players without game files: "Fps Rig" by J-Toastie (poly.pizza, CC-BY 3.0),
// gloved arms holding a Glock 19 with Idle/Shoot/Reload animations. The pistol view uses the whole
// rig; the knife view uses a copy with the gun hidden, the left arm folded away and the karambit
// in the right fist. Credits: public/licenses/fps-rig-CC-BY-3.0.txt and the menu's credits line.

import * as THREE from 'three';
import { attachFpArmsToPistol, loadFpArmsKnife } from './fpArms';
import { fitUsp } from './uspModel';
import { P_BEND, P_DX, P_DY, P_DZ, P_PITCH, P_ROLL, P_SIDE, P_TWIST, P_YAW } from './knifeAnim';

export const FPS_RIG_URL = `${import.meta.env.BASE_URL ?? '/'}assets/models/fps-rig.glb`;

/** Rig units (arms point +X, Y up, Z right) -> viewmodel camera units (-Z forward, X right). */
const RIG_SCALE = 0.055;
/** Where the camera sits in rig space, per weapon (tuned by eye). */
const PISTOL_EYE = new THREE.Vector3(-9.0, 3.1, 0.2);
const KNIFE_EYE = new THREE.Vector3(-9.0, 3.1, -1.5);
/** Knife view: fist turned so the blade curves forward, toward the middle of the screen. */
const KNIFE_TURN = new THREE.Euler(0.9, 0.3, -0.4);
const KNIFE_SHIFT = new THREE.Vector3(-0.03, 0.06, 0);
/** Karambit size in camera units, and the muzzle in the barrel bone's frame. */
const KARAMBIT_SCALE = 0.11;
const MUZZLE = new THREE.Vector3(0, 0.0052, 0.0003);
/** Silencer length along the barrel bone (the muzzle flash moves to its end). */
export const SILENCER_LENGTH = 0.0117;

export interface RigPistol {
  /** Camera-space holder: position it for bob/sway/raise. */
  object: THREE.Group;
  mixer: THREE.AnimationMixer;
  idle: THREE.AnimationAction;
  shoot: THREE.AnimationAction;
  reload: THREE.AnimationAction;
  reloadClipSeconds: number;
  silencer: THREE.Object3D;
  /** Show or hide the silencer and move the muzzle flash to match. */
  setSilenced(on: boolean): void;
  /** Show the loose magazine (only while reloading, when the gun's own is replaced). */
  showMagazine(on: boolean): void;
  /** Muzzle flash parent (at the barrel or the silencer's end). */
  muzzle: THREE.Object3D;
  /** Stop everything and put the bones back (an interrupted reload leaves the magazine out). */
  resetPose(): void;
  /** Turn the gun in the hand about its grip: yaw (+ muzzle left), roll, lift, pitch (+ muzzle up). */
  turnGun(yaw: number, roll: number, lift: number, pitch?: number): void;
  /** The leather-glove arms (fpArms.ts): follow the rig's hands after its animation each frame. */
  arms: { follow: () => void; showSupport: (on: boolean) => void } | null;
}

export interface RigKnife {
  /** Camera-space arm with the karambit, at its resting place. */
  object: THREE.Group;
  /** The knife in the hand (its rest placement is where other arms put their knife too). */
  knife: THREE.Object3D;
  /** Apply a knife pose (knifeAnim.ts): arm swing around the shoulder, wrist bend and twist. */
  pose(p: readonly number[], time?: number): void;
}

export interface FpsRig {
  pistol: RigPistol;
  knife: RigKnife;
}

let loading: Promise<FpsRig | null> | null = null;

/**
 * Load the rig once. `karambit` is the procedural knife (meshes only, materials shared so finishes
 * keep working); it is cloned into the knife arm's fist.
 */
export function loadFpsRig(karambit: THREE.Object3D, slideMat?: THREE.Material): Promise<FpsRig | null> {
  loading ??= (async () => {
    try {
      const [{ GLTFLoader }, SkeletonUtils] = await Promise.all([
        import('three/addons/loaders/GLTFLoader.js'),
        import('three/addons/utils/SkeletonUtils.js'),
      ]);
      const gltf = await new GLTFLoader().loadAsync(FPS_RIG_URL);
      const clip = (name: string) => gltf.animations.find((a) => a.name.endsWith(name)) ?? null;
      const idle = clip('Idle');
      const shoot = clip('Shoot');
      const reload = clip('Reload');
      const grip = clip('Grip');
      if (!idle || !shoot || !reload || !grip) return null;
      tuneMaterials(gltf.scene);
      const pistolScene = SkeletonUtils.clone(gltf.scene);
      const pistol = buildPistol(pistolScene, idle, shoot, reload);
      // The USP-S model (uspModel.ts) replaces the rig's Glock when it loads.
      if (slideMat) await fitUsp(pistol, pistolScene, slideMat);
      pistol.arms = await attachFpArmsToPistol(pistol, pistolScene);
      const knife = buildKnife(SkeletonUtils.clone(gltf.scene), grip, karambit);
      // The leather-glove arms (fpArms.ts) hold the knife when they load; they put it where this
      // arm's knife rests.
      knife.object.updateMatrixWorld(true);
      const arms = await loadFpArmsKnife(karambit);
      return { pistol, knife: arms ?? knife };
    } catch {
      return null;
    }
  })();
  return loading;
}

/** Plain colours from the file read as fairly metallic; arms should look like cloth and skin. */
function tuneMaterials(scene: THREE.Object3D) {
  scene.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    o.frustumCulled = false;
    for (const m of Array.isArray(o.material) ? o.material : [o.material]) {
      if (!(m instanceof THREE.MeshStandardMaterial)) continue;
      const gun = /Frame|Slide|Black|Gray|Magazine|Bullet|White/.test(m.name);
      m.metalness = gun ? 0.3 : 0;
      m.roughness = Math.max(0.45, m.roughness);
      // Dark sleeve and brown leather gloves, like the knife arms (fpArms.ts).
      if (m.name === 'Shirt') m.color.set(0x1d1a16);
      if (m.name === 'Glove') m.color.set(0x3b2b1e);
    }
  });
}

/** Holder in camera space with the rig placed so the camera sits at `eye` in rig space. */
function place(scene: THREE.Object3D, eye: THREE.Vector3): { holder: THREE.Group; rig: THREE.Group } {
  const rig = new THREE.Group();
  rig.add(scene);
  rig.rotation.set(0, Math.PI / 2, 0);
  rig.scale.setScalar(RIG_SCALE);
  rig.updateMatrix();
  const e = eye.clone().applyMatrix4(rig.matrix);
  rig.position.copy(e).negate();
  const holder = new THREE.Group();
  holder.add(rig);
  return { holder, rig };
}

export const byName = (root: THREE.Object3D, name: string) => {
  const o = root.getObjectByName(name);
  if (!o) throw new Error('fps rig: no ' + name);
  return o;
};

function buildPistol(
  scene: THREE.Object3D,
  idleClip: THREE.AnimationClip,
  shootClip: THREE.AnimationClip,
  reloadClip: THREE.AnimationClip,
): RigPistol {
  const { holder, rig } = place(scene, PISTOL_EYE);
  const mixer = new THREE.AnimationMixer(scene);
  const idle = mixer.clipAction(idleClip);
  idle.play();
  const shoot = mixer.clipAction(shootClip);
  shoot.setLoop(THREE.LoopOnce, 1);
  const reload = mixer.clipAction(reloadClip);
  reload.setLoop(THREE.LoopOnce, 1);
  for (const a of [shoot, reload]) a.clampWhenFinished = false;
  const bones: [THREE.Object3D, THREE.Vector3, THREE.Quaternion][] = [];
  scene.traverse((o) => {
    if (o instanceof THREE.Bone) bones.push([o, o.position.clone(), o.quaternion.clone()]);
  });
  const resetPose = () => {
    mixer.stopAllAction();
    for (const [b, p, q] of bones) {
      b.position.copy(p);
      b.quaternion.copy(q);
    }
    idle.reset().play();
  };

  // Silencer (the game's pistol starts silenced, USP-S style), on the barrel so it moves with it.
  const barrel = byName(scene, 'Barrel');
  const mat = new THREE.MeshStandardMaterial({ color: 0x15171a, metalness: 0.3, roughness: 0.55 });
  const silencer = new THREE.Group();
  const len = SILENCER_LENGTH;
  const tube = new THREE.Mesh(new THREE.CylinderGeometry(0.0012, 0.0012, len, 24), mat);
  tube.position.y = len / 2;
  const ring = new THREE.Mesh(new THREE.CylinderGeometry(0.00129, 0.00129, 0.0008, 24), mat);
  ring.position.y = 0.0005;
  silencer.add(tube, ring);
  silencer.position.copy(MUZZLE);
  barrel.add(silencer);
  for (const m of [tube, ring]) m.frustumCulled = false;
  const muzzle = new THREE.Object3D();
  barrel.add(muzzle);
  muzzle.position.copy(MUZZLE);
  // Flash: built pointing down -Z; the barrel points along its +Y.
  muzzle.rotation.x = Math.PI / 2;
  return {
    object: holder,
    mixer,
    idle,
    shoot,
    reload,
    reloadClipSeconds: reloadClip.duration,
    silencer,
    muzzle,
    setSilenced(on: boolean) {
      this.silencer.visible = on;
      muzzle.position.y = silencer.position.y + (on ? SILENCER_LENGTH : 0);
    },
    resetPose,
    showMagazine() {},
    turnGun: gunTurner(holder, rig, scene),
    arms: null,
  };
}

/** Rotates the rig (gun and the rig's hands) about the gun's grip, without allocating. */
function gunTurner(holder: THREE.Group, rig: THREE.Group, scene: THREE.Object3D) {
  holder.updateMatrixWorld(true);
  const grip = holder.worldToLocal(byName(scene, 'Magazine').getWorldPosition(new THREE.Vector3()));
  rig.updateMatrix();
  const rest = rig.matrix.clone();
  rig.matrixAutoUpdate = false;
  const m = new THREE.Matrix4();
  const e = new THREE.Euler(0, 0, 0, 'YZX');
  return (yaw: number, roll: number, lift: number, pitch = 0) => {
    m.makeRotationFromEuler(e.set(pitch, yaw, roll, 'YZX'));
    m.setPosition(grip.x, grip.y + lift, grip.z);
    rig.matrix.makeTranslation(-grip.x, -grip.y, -grip.z).premultiply(m).multiply(rest);
    rig.matrixWorldNeedsUpdate = true;
  };
}

function buildKnife(
  scene: THREE.Object3D,
  gripClip: THREE.AnimationClip,
  karambit: THREE.Object3D,
): RigKnife {
  const { holder } = place(scene, KNIFE_EYE);
  byName(scene, 'Glock19').visible = false;
  // Fold the left arm away (it has nothing to hold).
  byName(scene, 'UpperArmL').scale.setScalar(1e-4);
  // Pose: the grip clip, then curl the trigger finger like the others.
  const mixer = new THREE.AnimationMixer(scene);
  mixer.clipAction(gripClip).play();
  mixer.update(0);
  mixer.stopAllAction();
  for (const [from, to] of [
    ['DoubleFingersBeginning001', 'IndexBeginningR001'],
    ['DoubleFingersR001', 'IndexR001'],
    ['DoubleFingersTipR001', 'IndexTipR001'],
  ])
    byName(scene, to).quaternion.copy(byName(scene, from).quaternion);
  holder.updateMatrixWorld(true);

  // Karambit in the fist: handle along the gun's grip axis (magazine bone), ring up by the index
  // finger, blade down with its hook toward where the barrel pointed.
  const w = (n: string) => byName(scene, n).getWorldPosition(new THREE.Vector3());
  const up = w('Magazine_end').sub(w('Magazine')).normalize();
  const fwd = w('Barrel_end').sub(w('Barrel')).normalize();
  fwd.addScaledVector(up, -fwd.dot(up)).normalize();
  const x = up.clone().negate();
  const y = fwd.clone().negate();
  const z = new THREE.Vector3().crossVectors(x, y);
  const fist = w('Magazine');
  const knifeWorld = new THREE.Matrix4().compose(
    fist,
    new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, y, z)),
    new THREE.Vector3(KARAMBIT_SCALE, KARAMBIT_SCALE, KARAMBIT_SCALE),
  );
  const gripBone = byName(scene, 'Root');
  const k = karambit.clone();
  gripBone.matrixWorld.clone().invert().multiply(knifeWorld).decompose(k.position, k.quaternion, k.scale);
  gripBone.add(k);

  // Turn the arm around the fist (blade forward).
  const pivot = new THREE.Group();
  const inner = holder;
  inner.position.sub(fist);
  pivot.add(inner);
  pivot.position.copy(fist).add(KNIFE_SHIFT);
  pivot.rotation.copy(KNIFE_TURN);
  return {
    ...animatedArm(pivot, scene, { shoulder: 'UpperArmR001', elbow: 'LowerArmR001', wrist: 'HandR001' }),
    knife: k,
  };
}

/**
 * Hook an arm up to the keyframed knife animations: `content` (the arm, placed in camera space at
 * rest) swings around the shoulder bone, and the wrist bone bends and twists the blade.
 */
export function animatedArm(
  content: THREE.Object3D,
  scene: THREE.Object3D,
  bones: { shoulder: string; elbow: string; wrist: string },
  /**
   * Bends the arm's bones after the swing and before the wrist turn (two-bone IK for inspects); true
   * if it changed them, so the wrist turn works from where the forearm is now.
   */
  bend?: () => boolean,
): { object: THREE.Group; pose(p: readonly number[]): void } {
  const rest = new THREE.Group();
  rest.add(content);
  rest.updateMatrixWorld(true);
  const wpos = (n: string) => byName(scene, n).getWorldPosition(new THREE.Vector3());
  const shoulder = wpos(bones.shoulder);
  const forearm = wpos(bones.wrist).sub(wpos(bones.elbow)).normalize();
  // Bend: around the axis across the forearm and the screen's up; side: the third axis.
  const bendAxis = new THREE.Vector3().crossVectors(forearm, new THREE.Vector3(0, 1, 0)).normalize();
  const sideAxis = new THREE.Vector3().crossVectors(bendAxis, forearm).normalize();
  const wrist = byName(scene, bones.wrist);
  const wristRest = wrist.quaternion.clone();
  // The wrist's parent orientation in this (camera-at-rest) space; the arm above it never moves.
  const parentQ = wrist.parent!.getWorldQuaternion(new THREE.Quaternion());
  const parentQInv = parentQ.clone().invert();
  const swing = new THREE.Group();
  swing.position.copy(shoulder);
  rest.position.copy(shoulder).negate();
  swing.add(rest);
  const object = new THREE.Group();
  object.add(swing);
  const qa = new THREE.Quaternion();
  const qb = new THREE.Quaternion();
  const qw = new THREE.Quaternion();
  // Bent arm: the wrist turn's axes and the wrist's parent frame from the bones as they are now, in
  // the swing's own space (where the resting values above were measured).
  const elbowBone = byName(scene, bones.elbow);
  const base = new THREE.Quaternion();
  const sw = new THREE.Quaternion();
  const pw = new THREE.Quaternion();
  const pq = new THREE.Quaternion();
  const pqInv = new THREE.Quaternion();
  const fa = new THREE.Vector3();
  const ba = new THREE.Vector3();
  const sa = new THREE.Vector3();
  const va = new THREE.Vector3();
  const vb = new THREE.Vector3();
  const UP = new THREE.Vector3(0, 1, 0);
  const pose = (p: readonly number[]) => {
    swing.position.set(shoulder.x + p[P_DX], shoulder.y + p[P_DY], shoulder.z + p[P_DZ]);
    swing.rotation.set(p[P_PITCH], p[P_YAW], p[P_ROLL], 'YXZ');
    if (bend) {
      object.updateMatrixWorld(true);
      if (bend()) {
        swing.getWorldQuaternion(sw).invert();
        fa.copy(wrist.getWorldPosition(va))
          .sub(elbowBone.getWorldPosition(vb))
          .applyQuaternion(sw)
          .normalize();
        ba.crossVectors(fa, UP).normalize();
        sa.crossVectors(ba, fa).normalize();
        pq.copy(sw).multiply(wrist.parent!.getWorldQuaternion(pw));
        pqInv.copy(pq).invert();
        base.copy(wrist.quaternion);
        qw.setFromAxisAngle(fa, p[P_TWIST]);
        qw.multiply(qa.setFromAxisAngle(ba, p[P_BEND]));
        qw.multiply(qb.setFromAxisAngle(sa, p[P_SIDE]));
        wrist.quaternion.copy(pqInv).multiply(qw).multiply(pq).multiply(base);
        return;
      }
    }
    // World-space wrist turn, carried into the bone's parent frame.
    qw.setFromAxisAngle(forearm, p[P_TWIST]);
    qw.multiply(qa.setFromAxisAngle(bendAxis, p[P_BEND]));
    qw.multiply(qb.setFromAxisAngle(sideAxis, p[P_SIDE]));
    wrist.quaternion.copy(parentQInv).multiply(qw).multiply(parentQ).multiply(wristRest);
  };
  return { object, pose };
}
